import React, { FC, useEffect, useState } from "react";
import Modal from "react-modal";
import { Flex } from "reflexbox";
import { useHistory, useLocation } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { useSwal } from "@hooks/useSwal";

import {
  AddButton,
  ScrollableList,
  ListColumn,
  ExpandableRow,
  Text,
  EditIcon,
  TrashIcon,
  ListContainer,
  Input,
  Filter,
} from "@icr/react.uicomponents";

import { deserialize } from "@api/jsonApiParser";
import { useFormatMessage } from "@hooks/formatMessage";

import "../../../styles/index.scss";
import TenantsModal from "./Modal/TenantModal";
import { TDispatch, TRootState } from "@redux/types";
import { getParameterByName } from "@utils/toSearchParams";
import {
  deleteTenant,
  fetchTenantList,
  getTenantById,
  incrementTenantsPageNo,
  setTenantsPageNo,
} from "./ducks";
import { fetchCompanies } from "@features/Company/ducks";

enum formMode {
  edit = "edit",
}

const Tenants: FC = () => {
  const [modalContent, setModalContent] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(undefined);
  const [addTenantForm, setAddTenantForm] = useState(undefined);
  const [genericTextFilter, setGenericTextFilter] = useState<string>(null);
  const [companyList, setCompanyList] = useState([]);
  const { swalConfirmationMessage, swalAlertWithCallback } = useSwal();

  const history = useHistory();
  const location = useLocation();
  const dispatch: TDispatch = useDispatch();
  const formatMessage = useFormatMessage();

  const { tenants, moreTenants, tenantsPageNo } = useSelector(
    ({ TenantReducer: { tenants, moreTenants, tenantsPageNo } }: TRootState) => ({
      tenants,
      moreTenants,
      tenantsPageNo,
    }),
    shallowEqual
  );

  if (location.search && location.search.length > 0) {
    setModalContent(getParameterByName("content", location.search));
    setModalIsOpen(!!modalContent);
  }

  useEffect(() => {
    dispatch(fetchTenantList()).then(() => dispatch(incrementTenantsPageNo()));
    dispatch(fetchCompanies()).then(res => setCompanyList(res));
  }, [dispatch]);

  const handleScroll = () => {
    if (moreTenants) {
      dispatch(fetchTenantList(tenantsPageNo, 10, { GenericText: genericTextFilter })).then(() =>
        dispatch(incrementTenantsPageNo())
      );
    }
  };

  const filterTenants = () => {
    dispatch(setTenantsPageNo(1));
    dispatch(fetchTenantList(1, 10, { GenericText: genericTextFilter }));
  };

  const clearFilters = () => {
    setGenericTextFilter(null);

    dispatch(setTenantsPageNo(1));
    dispatch(fetchTenantList());
  };

  const closeModal = () => {
    setModalIsOpen(false);
    history.push({ search: "" });
    filterTenants();
  };

  const openModal = (content, message?, tenant?) => {
    getTenantById(tenant.id).then(res => {
      if (res.success) {
        setAddTenantForm(deserialize(res.data));
        setModalIsOpen(true);
        setModalContent(content);
      }
    });
  };

  const saveHistoryAndFilterTenants = () => {
    history.push({
      search: "",
    });
    filterTenants();
  };

  const showSuccessMessage = (message: string) => {
    swalAlertWithCallback(
      {
        text: `Tenant has been ${message}!`,
        type: "success",
        confirmationText: "Ok!",
      },
      saveHistoryAndFilterTenants
    );
  };

  const remove = id => {
    swalConfirmationMessage({
      text: "You really want to delete this tenant?",
      type: "info",
      cancelationText: "Cancel",
      confirmationText: "Remove tenant",
    }).then(value => {
      if (value) {
        deleteTenant(id).then(res => {
          if (res.success) showSuccessMessage("deleted");
        });
      }
    });
  };

  const renderModalContent = () =>
    modalIsOpen && (
      <TenantsModal closeModal={closeModal} initialData={addTenantForm} companyList={companyList} />
    );

  return (
    <ListContainer>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Large Modal"
        className="large-modal"
        overlayClassName="large-modal-overlay"
      >
        {renderModalContent()}
      </Modal>

      <Flex pl={["10px", "80px"]} pr={["10px", 0]} height="100%" width="100%">
        <Flex flexDirection="column" width="100%">
          <AddButton
            id="addTenantBtn"
            p="0 32px"
            m="32px 0"
            label={formatMessage("app.addNewTenant")}
            onClick={() => {
              setAddTenantForm({});
              setModalIsOpen(true);
            }}
          />
          <ScrollableList
            items={tenants}
            onLoadMore={handleScroll}
            noItemsFoundMessage={formatMessage("app.noRecordAvailable")}
            renderHeader={
              <Flex width="100%">
                <ListColumn>
                  <Text>{formatMessage("app.code")}</Text>
                </ListColumn>
                <ListColumn>
                  <Text>{formatMessage("app.name")}</Text>
                </ListColumn>
                <ListColumn>
                  <Text>{formatMessage("app.description")}</Text>
                </ListColumn>
                <ListColumn>
                  <Text>{formatMessage("app.company")}</Text>
                </ListColumn>
                <ListColumn>
                  <Text>{formatMessage("app.insuranceId")}</Text>
                </ListColumn>
                <ListColumn></ListColumn>
              </Flex>
            }
            renderItem={(item, index) => (
              <ExpandableRow
                key={"tenantRow" + index}
                id={item.id as string}
                expandable={false}
                top={
                  <Flex width="100%">
                    <ListColumn>
                      <Text font="semiBold">{item.tenantCode}</Text>
                    </ListColumn>
                    <ListColumn>
                      <Text>{item.tenantName}</Text>
                    </ListColumn>
                    <ListColumn>
                      <Text>{item.description}</Text>
                    </ListColumn>
                    <ListColumn>
                      <Text>{item.companyName}</Text>
                    </ListColumn>
                    <ListColumn>
                      <Text>{item.insuranceId}</Text>
                    </ListColumn>
                    <ListColumn icon>
                      <EditIcon
                        id={"changeEntry" + item.id}
                        title="Edit Tenant"
                        onClick={() => openModal(formMode.edit, "", item)}
                      />
                      <TrashIcon
                        id={"deleteEntry" + item.id}
                        title="Delete Tenant"
                        onClick={() => remove(item.id)}
                      />
                    </ListColumn>
                  </Flex>
                }
              />
            )}
          />
        </Flex>
        <Filter
          id="filterArea"
          searchButtonText={formatMessage("app.search", "Search")}
          onClickSearchButton={filterTenants}
          clearButtonText={formatMessage("app.clear", "Clear")}
          onClickClearButton={clearFilters}
          open={true}
        >
          <Input
            id="dynamicSearch"
            label="Dynamic Filter"
            value={genericTextFilter}
            placeholder={formatMessage("app.anyfield", "Any Field")}
            onChange={e => setGenericTextFilter(e.target.value)}
          />
        </Filter>
      </Flex>
    </ListContainer>
  );
};

export default Tenants;
